import { Input } from '../components/forms/index.tsx';
import { CodeVerification, type PendingEmail } from './auth/CodeVerification.tsx';
import { useState, type FormEvent } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { Me } from '@replay/shared';
import { api, errorMessage } from '../lib/api.ts';
import { useAuth } from '../lib/auth.tsx';
import { Loading } from '../components/ui.tsx';
import { AuthLayout } from './Login.tsx';

export function Invite() {
  const { token = '' } = useParams();
  const { me } = useAuth();
  const qc = useQueryClient();
  const nav = useNavigate();
  const inv = useQuery({
    queryKey: ['invite', token],
    queryFn: () => api<{ invitedBy: string; expiresAt: string }>(`/api/auth/invite/${encodeURIComponent(token)}`),
    retry: false,
  });
  const [form, setForm] = useState({ username: '', displayName: '', email: '', password: '', confirm: '' });
  const [pending, setPending] = useState<PendingEmail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (me) return <Navigate to="/" replace />;
  if (inv.isLoading) return <Loading />;
  if (inv.error) {
    return (
      <AuthLayout title="Invitación no válida" lead={errorMessage(inv.error)}>
        <p className="muted">Pide a quien te invitó un enlace nuevo. Si ya tienes cuenta, entra directamente.</p>
        <Link className="btn" to="/login" data-autofocus>
          Ir a iniciar sesión
        </Link>
      </AuthLayout>
    );
  }

  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (form.password !== form.confirm) return setError('Las contraseñas no coinciden');
    setBusy(true);
    setError(null);
    try {
      const r = await api<PendingEmail>('/api/auth/register', {
        body: { email: form.email, token, username: form.username, displayName: form.displayName || form.username, password: form.password },
      });
      setPending(r);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  if (pending) return <AuthLayout title="Revisa tu correo" lead={`Hemos enviado un código a ${pending.email}`}>
    <CodeVerification pending={pending} onPending={setPending} verifyUrl="/api/auth/register/verify" resendUrl="/api/auth/register/resend" onSuccess={(r) => { qc.setQueryData(['me'], (r as { me: Me }).me); nav('/', { replace: true }); }} />
    <button className="btn btn-ghost" onClick={() => { setPending(null); setForm({ ...form, password: '', confirm: '' }); setError(null); }}>Cambiar correo</button>
  </AuthLayout>;

  return (
    <AuthLayout
      title="Crea tu cuenta"
      lead={
        inv.data!.invitedBy === 'REPLAY' ? 'Esta invitación crea la cuenta de administración de tu REPLAY.' : `${inv.data!.invitedBy} te ha invitado a REPLAY.`
      }
    >
      <form className="form-grid" onSubmit={submit}>
        <div className="field">
          <label htmlFor="inv-user">Nombre de usuario</label>
          <Input
            id="inv-user"
            className="input"
            autoComplete="username"
            value={form.username}
            onChange={set('username')}
            required
            minLength={3}
            maxLength={24}
            pattern="[a-zA-Z0-9_.\-]+"
            aria-describedby="inv-user-hint"
            autoFocus
            data-autofocus
          />
          <small id="inv-user-hint">Letras, números, punto, guion o guion bajo. Lo usarás para entrar.</small>
        </div>
        <label className="field">
          <span>Cómo quieres que te vean</span>
          <Input className="input" value={form.displayName} onChange={set('displayName')} maxLength={40} placeholder={form.username || 'Tu nombre'} />
        </label>
        <label className="field"><span>Correo electrónico</span><Input type="email" autoComplete="email" maxLength={254} required value={form.email} onChange={set('email')} /></label>
        <div className="field">
          <label htmlFor="inv-pass">Contraseña</label>
          <Input
            id="inv-pass"
            className="input"
            type="password"
            autoComplete="new-password"
            value={form.password}
            onChange={set('password')}
            required
            minLength={10}
            aria-describedby="inv-pass-hint"
          />
          <small id="inv-pass-hint">Mínimo 10 caracteres.</small>
        </div>
        <label className="field">
          <span>Repite la contraseña</span>
          <Input className="input" type="password" autoComplete="new-password" value={form.confirm} onChange={set('confirm')} required />
        </label>
        {error && (
          <div className="error-box" role="alert">
            {error}
          </div>
        )}
        <button className="btn btn-primary btn-lg" disabled={busy}>
          {busy ? 'Creando cuenta…' : 'Crear cuenta'}
        </button>
      </form>
    </AuthLayout>
  );
}
